/**
 * Idle session manager — M2.11.
 *
 * Port of legacy `session-idle.component.ts`. Drives the "you're about to be
 * signed out" warning + auto-logout flow.
 *
 * Contract anchors (.docs/dual-shell-contract.md §3, §5):
 *   - T_idle = 1720s, T_warn = 60s   — locked, must match legacy.
 *   - `portal.idle.lastActivity` localStorage key — shared last-activity
 *     timestamp. Any tab's activity keeps ALL tabs alive (legacy
 *     NgIdleStorage parity). The idle threshold + warning countdown are
 *     evaluated against this shared value, polled once per second, so a stale
 *     background tab can never independently time out the active session.
 *   - `idle-nudge` BroadcastChannel  — payload {v:1, userActivityAt:number},
 *     throttled 1/30s per tab. Cross-shell activity signal; receivers fold it
 *     into the shared timestamp.
 *   - `logout_channel` BroadcastChannel — payload
 *     {v?:1, action:'logout'|'sessionLogout', ...} — other tabs follow.
 *   - Web Lock `'portal-tab'`         — best-effort holder; failure is logged
 *     but never forces logout.
 *
 * Activity events match legacy `EventTargetInterruptSource` set:
 * mousemove / mousedown / mouseup / click / keydown on `document`.
 *
 * Lifecycle:
 *   start()  — installed by ShellLayout when an active MSAL account exists.
 *   stop()   — on unmount or sign-out; tears down listeners + channels.
 *   continueSession() — bound to the "Stay signed in" button in the modal.
 *
 * State subscription:
 *   subscribe(cb)  — useSyncExternalStore-friendly signature.
 *   getState()     — synchronous current state.
 */

import { authModule, clearAllStorage } from './auth-module';
import { msalInstance } from './msal';
import { goToLoginInteractive } from './interaction-required';
import { getTracker } from '@/telemetry/tracker';

// Timing constants from .docs/dual-shell-contract.md §5 (locked).
// Configurable via env for test overrides only; defaults match legacy.
const T_IDLE_MS = Number(import.meta.env.VITE_IDLE_T_IDLE_MS) || 1720 * 1000; // 28m 40s
const T_WARN_MS = Number(import.meta.env.VITE_IDLE_T_WARN_MS) || 60 * 1000; // 60s
const NUDGE_THROTTLE_MS = 30_000; // 1/30s per dual-shell-contract §5
/** Poll cadence for the shared-activity idle check + warning countdown. */
const POLL_INTERVAL_MS = 1000;
/** Throttle localStorage writes — the poll only needs ~1s granularity. */
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

const IDLE_NUDGE_CHANNEL = 'idle-nudge';
const LOGOUT_CHANNEL = 'logout_channel';
const WEB_LOCK_NAME = 'portal-tab';
/**
 * Shared last-activity timestamp (epoch ms). Written by every tab on user
 * activity; read by all tabs in `readSharedActivity()`. This is the localStorage
 * equivalent of legacy `NgIdleStorage` and is what keeps background tabs alive.
 */
const IDLE_ACTIVITY_KEY = 'portal.idle.lastActivity';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'mouseup', 'click', 'keydown'] as const;

export type IdleStatus = 'inactive' | 'warned' | 'expired';

export interface IdleState {
  status: IdleStatus;
  /** Milliseconds left until auto-logout while status === 'warned'. */
  remainingMs: number;
}

interface IdleNudgeMessage {
  v: 1;
  userActivityAt: number;
}

interface LogoutMessage {
  v?: 1;
  action: 'logout' | 'sessionLogout';
  currentClient?: string;
  previousClient?: string;
}

class IdleManager {
  private running = false;
  /** Periodic check of shared last-activity vs T_idle and the warn deadline. */
  private pollTimer: number | null = null;
  private warnDeadline = 0;
  /**
   * When this tab entered the warning state. The poll cancels the warning if
   * the shared last-activity advances past this (another tab/shell became
   * active) — mirrors legacy ng-idle, where an interrupt in any tab resets
   * idle for all tabs.
   */
  private warnEnteredAt = 0;
  /**
   * This tab's own most-recent activity, updated synchronously on every DOM
   * event. Merged with the cross-tab localStorage value in
   * `readSharedActivity()` so sub-second local activity is never lost to the
   * write throttle.
   */
  private localLastActivity = Date.now();
  private lastActivityWriteAt = 0;
  private lastNudgeSentAt = 0;

  private nudgeChannel: BroadcastChannel | null = null;
  private logoutChannel: BroadcastChannel | null = null;
  private releaseWebLock: (() => void) | null = null;

  private state: IdleState = { status: 'inactive', remainingMs: 0 };
  private listeners = new Set<(state: IdleState) => void>();

  start(): void {
    if (this.running) return;
    this.running = true;

    try {
      this.nudgeChannel = new BroadcastChannel(IDLE_NUDGE_CHANNEL);
      this.nudgeChannel.addEventListener('message', this.onRemoteNudge);
      this.logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
      this.logoutChannel.addEventListener('message', this.onRemoteLogout);
    } catch {
      // BroadcastChannel unavailable (rare embedded contexts). Each tab will
      // time out independently — acceptable per contract §5.
    }

    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, this.onActivity, {
        passive: true,
        capture: true,
      });
    }
    // Cross-tab activity sync: any tab writing the shared last-activity key
    // fires a `storage` event in EVERY other tab. This keeps a background tab
    // alive while the user is active in another (legacy NgIdleStorage parity)
    // — strictly more reliable than the ephemeral `idle-nudge` broadcast,
    // which a freshly-woken tab can miss.
    window.addEventListener('storage', this.onStorage);

    void this.acquireWebLock();
    this.registerActivity({ broadcast: false });
    this.pollTimer = window.setInterval(this.poll, POLL_INTERVAL_MS);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;

    for (const ev of ACTIVITY_EVENTS) {
      document.removeEventListener(ev, this.onActivity, { capture: true });
    }
    window.removeEventListener('storage', this.onStorage);
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.nudgeChannel) {
      this.nudgeChannel.removeEventListener('message', this.onRemoteNudge);
      this.nudgeChannel.close();
      this.nudgeChannel = null;
    }
    if (this.logoutChannel) {
      this.logoutChannel.removeEventListener('message', this.onRemoteLogout);
      this.logoutChannel.close();
      this.logoutChannel = null;
    }
    if (this.releaseWebLock) {
      this.releaseWebLock();
      this.releaseWebLock = null;
    }
    this.setState({ status: 'inactive', remainingMs: 0 });
  }

  /** Called by the "Stay signed in" button in `SessionIdleModal`. */
  continueSession(): void {
    if (this.state.status !== 'warned') return;
    getTracker().event('session_idle_continue');
    this.registerActivity({ broadcast: true });
    this.setState({ status: 'inactive', remainingMs: 0 });
    // Legacy parity: refresh access token on continue so subsequent MFE
    // calls don't trip an expired token. Failure → show the login page with a
    // sign-in button (legacy `session-idle.resetIdle` error path), NOT the AAD
    // logout screen.
    void authModule.getTokenAsync().catch(() => {
      this.redirectToInteractiveLogin();
    });
  }

  /** Bound to the "Sign out" button in the modal. */
  expireNow(): void {
    this.localLogout('sessionLogout');
  }

  getState(): IdleState {
    return this.state;
  }

  subscribe(cb: (state: IdleState) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  // -------------------------------------------------------- internals --

  private readonly onActivity = (): void => {
    // Don't reset while the warning modal is open — user must click Continue
    // explicitly. Legacy uses `clearInterrupts()` for the same effect.
    if (this.state.status !== 'inactive') return;
    this.registerActivity({ broadcast: true });
  };

  private readonly onRemoteNudge = (e: MessageEvent<unknown>): void => {
    const msg = e.data as IdleNudgeMessage | null;
    if (!msg || msg.v !== 1 || typeof msg.userActivityAt !== 'number') return;
    // Cross-shell activity (the other shell may not write our localStorage
    // key). Feed it into the shared model so our tabs stay in sync; never
    // re-broadcast (would amplify into a loop).
    this.registerExternalActivity(msg.userActivityAt);
  };

  /**
   * Cross-tab last-activity update via the `storage` event. Fires in every
   * tab EXCEPT the writer, so it's how a background tab learns the active tab
   * is alive. Cancels an in-progress warning immediately when activity is
   * fresh; otherwise the next poll picks up the new shared timestamp.
   */
  private readonly onStorage = (e: StorageEvent): void => {
    if (e.key !== IDLE_ACTIVITY_KEY || e.newValue == null) return;
    const ts = Number(e.newValue);
    if (!Number.isFinite(ts)) return;
    this.registerExternalActivity(ts);
  };

  private readonly onRemoteLogout = (e: MessageEvent<unknown>): void => {
    const msg = e.data as LogoutMessage | null;
    if (!msg || (msg.action !== 'logout' && msg.action !== 'sessionLogout')) {
      return;
    }
    if (this.state.status === 'expired') return;
    this.setState({ status: 'expired', remainingMs: 0 });
    getTracker().event('session_idle_remote_logout', { action: msg.action });
    // Clear all storage so MSAL cached accounts are removed in this tab too.
    clearAllStorage();
    if (msg.action === 'sessionLogout') {
      if (msg.currentClient) localStorage.setItem('currentlySelectedClientId', msg.currentClient);
      if (msg.previousClient)
        localStorage.setItem('previouslySelectedClientId', msg.previousClient);
    }
    // Don't re-post — peer already broadcast. Legacy peer tabs navigate to the
    // shell login page with the sign-in button instead of launching another
    // Microsoft logout redirect.
    goToLoginInteractive();
  };

  /** Local user activity → bump the shared timestamp + (optionally) broadcast. */
  private registerActivity({ broadcast }: { broadcast: boolean }): void {
    const now = Date.now();
    this.localLastActivity = now;
    this.writeSharedActivity(now);
    if (broadcast) this.broadcastNudge(now);
  }

  /**
   * Activity observed from another tab/shell (storage event or nudge). Updates
   * the in-memory mirror and cancels a fresh warning. Never broadcasts.
   */
  private registerExternalActivity(ts: number): void {
    if (ts > this.localLastActivity) this.localLastActivity = ts;
    if (this.state.status === 'warned' && Date.now() - ts < T_IDLE_MS) {
      this.cancelWarning();
    }
  }

  private broadcastNudge(now: number): void {
    if (!this.nudgeChannel) return;
    if (now - this.lastNudgeSentAt < NUDGE_THROTTLE_MS) return;
    this.lastNudgeSentAt = now;
    try {
      this.nudgeChannel.postMessage({ v: 1, userActivityAt: now } satisfies IdleNudgeMessage);
    } catch {
      // Channel closed mid-flight; ignore.
    }
  }

  /**
   * Single timer driving both the idle threshold and the warning countdown,
   * evaluated against the SHARED last-activity timestamp so any tab's activity
   * keeps this tab alive.
   */
  private readonly poll = (): void => {
    if (this.state.status === 'expired') return;
    const lastActivity = this.readSharedActivity();

    if (this.state.status === 'inactive') {
      if (Date.now() - lastActivity >= T_IDLE_MS) this.enterWarning();
      return;
    }

    // status === 'warned'
    if (lastActivity > this.warnEnteredAt) {
      // Another tab/shell became active after we warned → recover.
      this.cancelWarning();
      return;
    }
    const rem = this.warnDeadline - Date.now();
    if (rem <= 0) {
      this.localLogout('sessionLogout');
      return;
    }
    this.setState({ status: 'warned', remainingMs: rem });
  };

  private enterWarning(): void {
    this.warnEnteredAt = Date.now();
    this.warnDeadline = this.warnEnteredAt + T_WARN_MS;
    this.setState({ status: 'warned', remainingMs: T_WARN_MS });
    getTracker().event('session_idle_warning_shown');
  }

  private cancelWarning(): void {
    if (this.state.status !== 'warned') return;
    this.warnDeadline = 0;
    this.setState({ status: 'inactive', remainingMs: 0 });
  }

  /** Most-recent activity across all tabs = max(localStorage, in-memory). */
  private readSharedActivity(): number {
    let shared = 0;
    try {
      const raw = localStorage.getItem(IDLE_ACTIVITY_KEY);
      if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n)) shared = n;
      }
    } catch {
      // localStorage unavailable (private mode / blocked) — fall back to the
      // in-memory value so this tab still times out independently.
    }
    return Math.max(shared, this.localLastActivity);
  }

  private writeSharedActivity(now: number): void {
    if (now - this.lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;
    this.lastActivityWriteAt = now;
    try {
      localStorage.setItem(IDLE_ACTIVITY_KEY, String(now));
    } catch {
      // Best-effort; cross-tab sync degrades to per-tab timers.
    }
  }

  /**
   * Token refresh failed on "Stay signed in" → take the user to the EY login
   * page with a sign-in button (legacy `session-idle.resetIdle` error path).
   * Unlike `localLogout`, this does NOT call `logoutRedirect` — the user is not
   * bounced through the AAD sign-out screen; they re-authenticate with a click.
   */
  private redirectToInteractiveLogin(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.setState({ status: 'expired', remainingMs: 0 });
    getTracker().event('session_idle_token_refresh_failed');
    clearAllStorage();
    goToLoginInteractive();
  }

  private localLogout(action: 'logout' | 'sessionLogout'): void {
    if (this.state.status === 'expired') return;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.setState({ status: 'expired', remainingMs: 0 });
    getTracker().event('session_idle_timeout', { action });

    // Capture account BEFORE clearing storage so logoutRedirect can
    // identify which account to sign out (avoids "Pick an account" prompt).
    const account = msalInstance.getActiveAccount() ?? undefined;

    // Broadcast on logout_channel so peer tabs follow. `sessionLogout`
    // variant preserves currentClient/previousClient so the next session
    // can restore — matches legacy `loginService.logOut(true, true)`.
    const msg: LogoutMessage = {
      v: 1,
      action,
      currentClient: localStorage.getItem('currentlySelectedClientId') ?? undefined,
      previousClient: localStorage.getItem('previouslySelectedClientId') ?? undefined,
    };
    try {
      this.logoutChannel?.postMessage(msg);
    } catch {
      // Best-effort.
    }

    // Clear all storage so MSAL cached accounts are removed and the next
    // login doesn't auto-redirect to home. Must happen after reading
    // clientIds above. Mirrors Angular `login.service.ts:loggedout()`.
    clearAllStorage();

    if (action === 'sessionLogout') {
      goToLoginInteractive();
      return;
    }

    void msalInstance.logoutRedirect({ account });
  }

  /**
   * Best-effort: hold the `'portal-tab'` Web Lock for the lifetime of the
   * page. The lock is informational — its absence does NOT prevent the idle
   * flow. Failure to acquire is logged via tracker only (per contract §5:
   * "Если acquire fail — не force-logout, warn + continue").
   */
  private async acquireWebLock(): Promise<void> {
    if (!('locks' in navigator)) return;
    try {
      await navigator.locks.request(WEB_LOCK_NAME, { ifAvailable: true }, async (lock) => {
        if (!lock) {

          return;
        }

        // Hold the lock until stop() releases the promise.
        await new Promise<void>((resolve) => {
          this.releaseWebLock = resolve;
        });
      });
    } catch (err) {
      getTracker().event('portal_tab_lock_error', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private setState(next: IdleState): void {
    this.state = next;
    for (const cb of this.listeners) cb(next);
  }
}

export const idleManager = new IdleManager();
